import { useContext } from 'react';

import { DataContext } from '@visx/xychart';

import { DEFAULT_X_TICKS, DEFAULT_Y_TICKS } from './utils';

function generateTickValues<T extends number | Date>(domain: T[], count: number): T[] {
  if (count === 1 && domain.length >= 1) {
    return [domain[0]!];
  }

  if (count === 2 && domain.length >= 2) {
    return [domain[0]!, domain[domain.length - 1]!];
  }

  const [min, max] = domain;

  // Handle Date objects (time scales)
  if (min instanceof Date && max instanceof Date) {
    const minTime = min.getTime();
    const maxTime = max.getTime();
    const step = (maxTime - minTime) / (count - 1);
    return Array.from({ length: count }, (_, i) => new Date(minTime + i * step)) as T[];
  }

  // Handle numbers (linear scales)
  if (typeof min === 'number' && typeof max === 'number') {
    const step = (max - min) / (count - 1);
    return Array.from({ length: count }, (_, i) => min + i * step) as T[];
  }

  // Unsupported types
  throw new Error(`Unsupported domain types for tick value generation: ${typeof min} and ${typeof max}`);
}

type UseTickValuesResult<XValue = unknown, YValue = unknown> = {
  xTickValues: XValue[] | undefined;
  yTickValues: YValue[] | undefined;
};

/**
 * Hook to generate tick values from the current XYChart scales
 * Ensures Grid and Axis components use the same tick values
 */
export function useTickValues<XValue extends Date | number, YValue extends Date | number>({
  numXTicks = DEFAULT_X_TICKS,
  numYTicks = DEFAULT_Y_TICKS,
}: { numXTicks?: number; numYTicks?: number } = {}): UseTickValuesResult<XValue, YValue> {
  const { xScale, yScale } = useContext(DataContext) || {};
  const xTickValues = xScale ? generateTickValues(xScale.domain() as XValue[], numXTicks) : undefined;
  const yTickValues = yScale ? generateTickValues(yScale.domain() as YValue[], numYTicks) : undefined;

  return { xTickValues, yTickValues };
}
